import type { ReactNode, RefObject } from 'react';
import { Box, Typography } from '@mui/material';
import { CardType, ContentCard } from 'src/core/ui/ContentCard';

const WarehouseRowEditor = ({ config }: { config: WarehouseRowEditorConfig }) => {
    const layout = config.layout;
    return <ContentCard cardId={config.id} md={12} cardTypes={[CardType.noHeader]}>
        <Box ref={config.formRef} data-warehouse-editor={config.id} sx={{ width: 1, minWidth: 0, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 1.5, containerType: 'inline-size' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', minWidth: 0 }}>
                <Box sx={{ flex: '1 1 360px', minWidth: 0, display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: `repeat(${config.summaries.length}, minmax(0, 1fr))` }, gap: 2 }}>
                    {config.summaries.map(summary => <Box key={summary.id} component="section" aria-label={summary.label} sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                            <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>{summary.title}</Typography>
                            {summary.actions && <WarehouseEditorActions actions={summary.actions} />}
                        </Box>
                        {summary.description && <Typography variant="body2" color="text.secondary">{summary.description}</Typography>}
                        {summary.details && <Box component="dl" sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.25, rowGap: 0.25, m: 0, color: 'text.secondary', fontSize: '0.8rem' }}>
                            {summary.details.map(detail => <Box key={detail.id} component="div" sx={{ display: 'inline-flex', flexWrap: 'wrap', columnGap: 0.5, minWidth: 0 }}>
                                <Box component="dt">{detail.label}:</Box>
                                <Box component="dd" data-field={detail.id} sx={{ m: 0, overflowWrap: 'anywhere' }}>{detail.value ?? '—'}</Box>
                            </Box>)}
                        </Box>}
                    </Box>)}
                </Box>
                {config.actions && <WarehouseEditorActions actions={config.actions} />}
            </Box>
            <Box component="form" aria-label={config.label} noValidate onSubmit={event => { event.preventDefault(); if (!config.disabled) void config.onSubmit?.(); }} onKeyDown={event => {
                if (!config.onSubmit || event.key !== 'Enter' || event.nativeEvent.isComposing || !(event.target instanceof HTMLInputElement)) return;
                if (config.submitOnEnter && !config.submitOnEnter.includes(event.target.name)) return;
                event.preventDefault();
                event.stopPropagation();
                if (config.disabled || event.repeat) return;
                void config.onSubmit();
            }} sx={{ overflowX: 'auto', maxWidth: 1, pt: 1.5 }}>
                <Box sx={{ display: 'grid', minWidth: layout.minWidth ?? 520, gridTemplateColumns: layout.compactColumns, gridTemplateAreas: layout.compactAreas, gap: 1, alignItems: 'start', '& .MuiGrid-item': { maxWidth: 'none', flexBasis: 'auto', minWidth: 0 }, '& .MuiFormControl-root': { my: 0 }, [`@container (min-width: ${layout.wideAt ?? 820}px)`]: { gridTemplateColumns: layout.columns, gridTemplateAreas: layout.areas } }}>
                    {config.fields.map(field => <Box key={field.id} sx={{ gridArea: field.id, minWidth: 0, ...(field.action ? { mt: 1 } : {}) }}>{field.content}</Box>)}
                </Box>
            </Box>
        </Box>
    </ContentCard>;
};

const WarehouseEditorActions = ({ actions }: { actions: readonly ReactNode[] }) => <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, minWidth: 0, '& .MuiButton-root': { whiteSpace: 'nowrap', mt: 0 } }}>
    {actions.map((action, index) => <Box key={index} sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center' }}>{action}</Box>)}
</Box>;

export type WarehouseRowEditorConfig = {
    id: string;
    label: string;
    formRef?: RefObject<HTMLDivElement>;
    summaries: readonly { id: string; label?: string; title: ReactNode; description?: ReactNode; details?: readonly { id: string; label: string; value: ReactNode }[]; actions?: readonly ReactNode[] }[];
    actions?: readonly ReactNode[];
    fields: readonly { id: string; content: ReactNode; action?: boolean }[];
    layout: { columns: string; areas: string; compactColumns: string; compactAreas: string; minWidth?: number; wideAt?: number };
    onSubmit?: () => void | Promise<void>;
    submitOnEnter?: readonly string[];
    disabled?: boolean;
};

export default WarehouseRowEditor;
